import type { Snapshot } from '../data/snapshotTypes'
import type { Game, GameResult, ID, Player, StatEvent, StatTypeId } from './types'
import { resolveResult } from './results'
import { statType } from './statTypes'
import { periodLabel } from './periods'

/**
 * All the season arithmetic, as pure functions over the local records plus the
 * PlayHQ snapshot.
 *
 * Two rules run through the whole file:
 *
 *  - `countsTowardLadder` is NEVER a filter. It describes PlayHQ's ladder
 *    accounting, not whether we played the game. A rebound in a grading round is
 *    a rebound. It is carried through only so a view can label it.
 *  - PlayHQ points and fouls never appear. This app is about the contributions
 *    the scoreboard does not record, and points live on the league website.
 */

export interface PlayerLine {
  player: Player
  gamesPlayed: number
  totals: Record<string, number>
  averages: Record<string, number>
  /** Best single game, and which game it was. */
  highs: Record<string, { value: number; gameId: ID | null }>
  /** Share of the team's total for each stat, 0-1. */
  share: Record<string, number>
}

export interface GameLine {
  game: Game
  result: GameResult | null
  awaitingResult: boolean
  countsTowardLadder: boolean
  opponentName: string
  totals: Record<string, number>
  playersPresent: number
  /** Per player, per stat, for this game. Keyed by player id. */
  perPlayer: Record<string, Record<string, number>>
}

export interface SplitLine {
  label: string
  games: number
  averages: Record<string, number>
}

export interface OpponentLine {
  name: string
  games: number
  wins: number
  losses: number
  draws: number
  awaiting: number
  averages: Record<string, number>
  /** Average minus the season average, per stat. Positive means better. */
  delta: Record<string, number>
}

export interface Insight {
  text: string
  /** How many games the observation rests on, so the UI can qualify it. */
  basis: number
}

export interface SeasonStats {
  statIds: StatTypeId[]
  gamesScored: number
  gamesWithResult: number
  players: PlayerLine[]
  games: GameLine[]
  teamTotals: Record<string, number>
  teamAverages: Record<string, number>
  winLoss: { wins: SplitLine; losses: SplitLine } | null
  periods: SplitLine[]
  opponents: OpponentLine[]
  insights: Insight[]
}

const EMPTY: SeasonStats = {
  statIds: [],
  gamesScored: 0,
  gamesWithResult: 0,
  players: [],
  games: [],
  teamTotals: {},
  teamAverages: {},
  winLoss: null,
  periods: [],
  opponents: [],
  insights: [],
}

function zero(ids: StatTypeId[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const id of ids) out[id] = 0
  return out
}

const round1 = (n: number) => Math.round(n * 10) / 10

export function buildSeasonStats(
  games: Game[],
  players: Player[],
  events: StatEvent[],
  snapshot: Snapshot | null,
  statIds: StatTypeId[],
): SeasonStats {
  // Only games that were actually scored in this app. A fixture we never opened
  // has no hustle stats and would drag every average toward zero.
  const scored = games
    .filter((g) => g.status === 'final')
    .sort((a, b) => a.date.localeCompare(b.date))
  if (scored.length === 0) return { ...EMPTY, statIds }

  const scoredIds = new Set(scored.map((g) => g.id))
  const live = events.filter((e) => !e.deleted && scoredIds.has(e.gameId))
  const playersById = new Map(players.map((p) => [p.id, p]))

  // --- per game -------------------------------------------------------------
  const gameLines: GameLine[] = scored.map((game) => {
    const resolved = resolveResult(game, snapshot)
    const totals = zero(statIds)
    const perPlayer: Record<string, Record<string, number>> = {}
    for (const playerId of game.roster) perPlayer[playerId] = zero(statIds)
    for (const e of live) {
      if (e.gameId !== game.id || !(e.statType in totals)) continue
      totals[e.statType]!++
      const row = perPlayer[e.playerId]
      if (row) row[e.statType]!++
    }
    return {
      perPlayer,
      game,
      result: resolved.result,
      awaitingResult: resolved.awaitingResult,
      countsTowardLadder: game.countsTowardLadder ?? true,
      opponentName: game.opponentName ?? 'Opponent',
      totals,
      playersPresent: game.roster.length,
    }
  })

  const teamTotals = zero(statIds)
  for (const line of gameLines) for (const id of statIds) teamTotals[id]! += line.totals[id]!
  const teamAverages = zero(statIds)
  for (const id of statIds) teamAverages[id] = round1(teamTotals[id]! / gameLines.length)

  // --- per player -----------------------------------------------------------
  const playerLines: PlayerLine[] = []
  const seen = new Set<ID>()
  for (const g of scored) for (const id of g.roster) seen.add(id)

  for (const playerId of seen) {
    const player = playersById.get(playerId)
    if (!player) continue
    // Present, not merely productive: a player who took no stats still played.
    const played = scored.filter((g) => g.roster.includes(playerId))
    const totals = zero(statIds)
    const highs: PlayerLine['highs'] = {}
    for (const id of statIds) highs[id] = { value: 0, gameId: null }

    for (const g of played) {
      for (const id of statIds) {
        const n = live.filter((e) => e.gameId === g.id && e.playerId === playerId && e.statType === id).length
        totals[id]! += n
        if (n > highs[id]!.value) highs[id] = { value: n, gameId: g.id }
      }
    }

    const averages = zero(statIds)
    const share = zero(statIds)
    for (const id of statIds) {
      averages[id] = played.length ? round1(totals[id]! / played.length) : 0
      share[id] = teamTotals[id]! > 0 ? totals[id]! / teamTotals[id]! : 0
    }
    playerLines.push({ player, gamesPlayed: played.length, totals, averages, highs, share })
  }
  playerLines.sort((a, b) => b.totals[statIds[0]!]! - a.totals[statIds[0]!]! || a.player.name.localeCompare(b.player.name))

  // --- wins vs losses -------------------------------------------------------
  const wins = gameLines.filter((g) => g.result === 'W')
  const losses = gameLines.filter((g) => g.result === 'L')
  const winLoss =
    wins.length > 0 && losses.length > 0
      ? { wins: splitOf('Wins', wins, statIds), losses: splitOf('Losses', losses, statIds) }
      : null

  // --- period split ---------------------------------------------------------
  const format = snapshot?.season.periodFormat ?? 'halves'
  const periodNumbers = [...new Set(live.map((e) => e.period))].sort((a, b) => a - b)
  const periods: SplitLine[] = periodNumbers.map((n) => {
    const averages = zero(statIds)
    for (const id of statIds) {
      averages[id] = round1(live.filter((e) => e.period === n && e.statType === id).length / gameLines.length)
    }
    return { label: periodLabel(format, n), games: gameLines.length, averages }
  })

  // --- opponents ------------------------------------------------------------
  const byOpponent = new Map<string, GameLine[]>()
  for (const line of gameLines) {
    const list = byOpponent.get(line.opponentName)
    if (list) list.push(line)
    else byOpponent.set(line.opponentName, [line])
  }
  const opponents: OpponentLine[] = [...byOpponent.entries()].map(([name, lines]) => {
    const averages = zero(statIds)
    const delta = zero(statIds)
    for (const id of statIds) {
      averages[id] = round1(lines.reduce((n, l) => n + l.totals[id]!, 0) / lines.length)
      delta[id] = round1(averages[id]! - teamAverages[id]!)
    }
    return {
      name,
      games: lines.length,
      wins: lines.filter((l) => l.result === 'W').length,
      losses: lines.filter((l) => l.result === 'L').length,
      draws: lines.filter((l) => l.result === 'D').length,
      awaiting: lines.filter((l) => l.awaitingResult).length,
      averages,
      delta,
    }
  })
  opponents.sort((a, b) => b.games - a.games || a.name.localeCompare(b.name))

  const stats: SeasonStats = {
    statIds,
    gamesScored: gameLines.length,
    gamesWithResult: gameLines.filter((g) => g.result !== null).length,
    players: playerLines,
    games: gameLines,
    teamTotals,
    teamAverages,
    winLoss,
    periods,
    opponents,
    insights: [],
  }
  stats.insights = buildInsights(stats)
  return stats
}

function splitOf(label: string, lines: GameLine[], statIds: StatTypeId[]): SplitLine {
  const averages = zero(statIds)
  for (const id of statIds) {
    averages[id] = round1(lines.reduce((n, l) => n + l.totals[id]!, 0) / lines.length)
  }
  return { label, games: lines.length, averages }
}

/**
 * Rule-based observations in plain language.
 *
 * Deliberately conservative. Each rule has a minimum number of games before it
 * will speak at all, none of them claim significance, and every one carries the
 * count it rests on so the view can say how thin the evidence is. With a handful
 * of games almost any difference is noise, and an app that says otherwise is
 * lying to its users.
 */
export function buildInsights(stats: SeasonStats): Insight[] {
  const out: Insight[] = []
  const { statIds, teamAverages, winLoss, games, players } = stats

  // Wins vs losses, only with at least two of each.
  if (winLoss && winLoss.wins.games >= 2 && winLoss.losses.games >= 2) {
    for (const id of statIds) {
      const w = winLoss.wins.averages[id]!
      const l = winLoss.losses.averages[id]!
      const gap = round1(w - l)
      // A gap smaller than a fifth of the season average is not worth a sentence.
      if (Math.abs(gap) < Math.max(1, teamAverages[id]! * 0.2)) continue
      const def = statType(id)
      out.push({
        text:
          gap > 0
            ? `Team ${def.label.toLowerCase()}s average ${w} in wins against ${l} in losses.`
            : `Team ${def.label.toLowerCase()}s average ${l} in losses against ${w} in wins.`,
        basis: winLoss.wins.games + winLoss.losses.games,
      })
    }
  }

  // Direction of travel over the recent run.
  if (games.length >= 5) {
    const recent = games.slice(-3)
    for (const id of statIds) {
      const recentAvg = round1(recent.reduce((n, g) => n + g.totals[id]!, 0) / recent.length)
      const gap = round1(recentAvg - teamAverages[id]!)
      if (Math.abs(gap) < Math.max(1, teamAverages[id]! * 0.25)) continue
      const def = statType(id)
      out.push({
        text: `${def.label}s over the last 3 games average ${recentAvg}, against a season average of ${teamAverages[id]}.`,
        basis: games.length,
      })
    }
  }

  // A standout individual game, only when it clearly beats the player's norm.
  for (const line of players) {
    if (line.gamesPlayed < 3) continue
    for (const id of statIds) {
      const high = line.highs[id]!
      if (high.value < 3 || high.value < line.averages[id]! * 1.8) continue
      const game = games.find((g) => g.game.id === high.gameId)
      if (!game) continue
      const def = statType(id)
      out.push({
        text: `${line.player.name}'s best game for ${def.label.toLowerCase()}s: ${high.value} against ${game.opponentName}.`,
        basis: line.gamesPlayed,
      })
      break // one standout per player is plenty
    }
  }

  return out.slice(0, 6)
}
